import { eachDayOfInterval, format, isValid } from 'date-fns';
import { User } from '../models/user.models';
import { REACT_APP_BUILD_ENV } from '../constants/server.constants';
import { ZoneDataProps } from '../models/analytics.models';

export const isEmailInvalid = (email: string): boolean => {
  const lastAtPos: number = email.lastIndexOf('@');
  const lastDotPos: number = email.lastIndexOf('.');
  return !(
    lastAtPos < lastDotPos &&
    lastAtPos > 0 &&
    email.indexOf('@@') == -1 &&
    lastDotPos > 2 &&
    email.length - lastDotPos > 2
  );
};

export const getCurrentTime = (): string => {
  return format(new Date(), `yyyy-MM-dd'T'HH:mm`);
};

export const getWeekInterval = (start: Date, end: Date): Date[] => {
  return eachDayOfInterval({
    start,
    end,
  });
};

export const formatDuration = (seconds: string | number): string => {
  if (!seconds) {
    return '00:00:00';
  }
  // Convert the input to a number
  const totalSeconds = parseInt(seconds.toString());

  // Calculate the hours, minutes, and remaining seconds
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  // Format the hours, minutes, and seconds as strings with leading zeros if necessary
  const formattedHours = hours.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const formattedSeconds = remainingSeconds.toString().padStart(2, '0');

  // Return the formatted time as a string
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export const formatDurationHHmm = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

export const formatThreadDate = (date: string): string => {
  const dateObj = new Date(date);
  const todaysDateObj = new Date(Date.now());
  ``;

  const fullDateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  const todaysDateOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  if (dateObj == todaysDateObj) {
    return `Today ${dateObj.toLocaleString(undefined, todaysDateOptions)}`;
  }

  return dateObj.toLocaleString(undefined, fullDateOptions);
};

/** Labels for thread audience (roles + group names). Null/empty → All Team. */
export const formatThreadAudienceLabels = (
  audience: { roles?: string[]; groupIds?: Array<string | number> } | null | undefined,
  groups: Array<{ id: string | number; name: string }> = []
): string[] => {
  if (
    !audience ||
    ((!audience.roles || audience.roles.length === 0) && (!audience.groupIds || audience.groupIds.length === 0))
  ) {
    return ['All Team'];
  }

  const labels: string[] = [];
  (audience.roles || []).forEach((role) => {
    if (role === 'coaches') labels.push('Coaches');
    else if (role === 'guardians') labels.push('Guardians');
  });
  (audience.groupIds || []).forEach((groupId) => {
    const group = groups.find((g) => String(g.id) === String(groupId));
    labels.push(group?.name ?? `Group ${groupId}`);
  });
  return labels.length > 0 ? labels : ['All Team'];
};

export function calculateTotalWeekDistance(response: any, groupId: string | undefined) {
  let initialValue = 0;

  for (const training of response.data.data) {
    if (training.trainingType === 'team') {
      if (groupId && training.excludedGroupIds.includes(groupId)) {
        continue;
      }
    }
    initialValue += training.distance;
  }
  return initialValue;
}

export function calculateTotalWeekDuration(response: any, groupId: string | undefined) {
  let initialValue = 0;

  for (const training of response.data.data) {
    if (training.trainingType === 'team') {
      if (groupId && training.excludedGroupIds.includes(groupId)) {
        continue;
      }
    }
    initialValue += training.duration;
  }
  return formatDuration(initialValue.toString());
}

export function getNearestMinute(seconds: string) {
  // Convert the input to a number
  const totalSeconds = parseInt(seconds);

  // Calculate the nearest minute by rounding the total seconds to the nearest minute
  const nearestMinute = Math.round(totalSeconds / 60);

  if (nearestMinute === 0) {
    return '';
  }

  // Return the nearest minute
  return nearestMinute.toString();
}

export const getMeasurementUnit = (): string => {
  const user: User = JSON.parse(localStorage.getItem('user') as string);

  if (user?.measurementSystem === 'metric') {
    return 'km';
  } else {
    return 'mi';
  }
};

export const isMeasurementSystemMetric = (): boolean => {
  const user: User = JSON.parse(localStorage.getItem('user') as string);

  return user?.measurementSystem === 'metric';
};

export const getElevationMeasurementUnit = (): string => {
  const user: User = JSON.parse(localStorage.getItem('user') as string);

  if (user?.measurementSystem === 'metric') {
    return 'm';
  } else {
    return 'ft';
  }
};

export const getSpeedMeasurementUnit = (): string => {
  const user: User = JSON.parse(localStorage.getItem('user') as string);

  if (user?.measurementSystem === 'metric') {
    return 'km/h';
  } else {
    return 'mi/h';
  }
};

export const createEmptyTrainingZones = (): ZoneDataProps => {
  return {
    zone0Duration: 0,
    zone1Duration: 0,
    zone2Duration: 0,
    zone3Duration: 0,
    zone4Duration: 0,
    zone5Duration: 0,
  };
};

export const redirectToNewTab = (url: string) => {
  const win: any = window.open(url, '_blank');
  win.focus();
};

export const isProd = () => {
  return REACT_APP_BUILD_ENV === 'prod';
};

export const safeDisplayValue = (value: number | string | undefined, unit?: string) => {
  if (unit) {
    return value ? `${value} ${unit}` : 'N/A';
  }

  return value ? value : 'N/A';
};

export const calculatePayment = (numMembers: number) => {
  const BASE_PRICE = 10;
  const PRICE_PER_ATHLETE = 3.99;

  const price = BASE_PRICE + (numMembers - 5) * PRICE_PER_ATHLETE;
  return Math.round(price * 100) / 100;
};

/** Parse ISO datetime string; returns display strings for date and time in the user's local timezone, or raw value if not parseable. */
const EQUIPMENT_PERFORMANCE_LABELS: Record<number, string> = {
  1: 'Bad',
  2: 'Okay',
  3: 'Good',
  4: 'Great',
  5: 'Excellent',
};

/**
 * Maps a raw equipment performance rating (1-5) to its display label,
 * matching the mapping used in the Race Log tab.
 */
export function getEquipmentPerformanceLabel(value?: number | null): string {
  if (value == null || !Number.isFinite(value)) return 'N/A';
  return EQUIPMENT_PERFORMANCE_LABELS[value] ?? String(value);
}

/**
 * Formats a race placement/result as an ordinal (1st, 2nd, 3rd, etc.),
 * matching the mapping used in the Race Log tab.
 */
export function getRaceResultOrdinal(value?: number | null): string {
  if (value == null || !Number.isFinite(value)) return 'N/A';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = value % 100;
  return `${value}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

export function parseRaceDateTime(raceDate: string): { dateDisplay: string; timeDisplay: string } {
  try {
    const d = new Date(raceDate);
    if (!isValid(d)) return { dateDisplay: raceDate, timeDisplay: '—' };
    return {
      dateDisplay: format(d, 'MM/dd/yyyy'),
      timeDisplay: format(d, 'h:mm a'),
    };
  } catch {
    return { dateDisplay: raceDate, timeDisplay: '—' };
  }
}

/**
 * Sorts an array of objects with name fields alphabetically by last name
 * If no space is found in the name, uses the full name for sorting
 * @param items Array of objects with name property
 * @returns Sorted array by last name
 */
export function sortByLastName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    // Extract last name from full name (assumes format "First Last" or "First Middle Last")
    const getLastName = (fullName: string): string => {
      const nameParts = fullName.trim().split(' ');
      // If only one name part, use the full name
      if (nameParts.length === 1) {
        return nameParts[0];
      }
      // Use the last part as last name
      return nameParts[nameParts.length - 1];
    };

    const lastNameA = getLastName(a.name).toLowerCase();
    const lastNameB = getLastName(b.name).toLowerCase();

    return lastNameA.localeCompare(lastNameB);
  });
}

export interface ZoneConfig {
  label: string;
  barColor: string;
  bg: string;
  borderColor: string;
  textColor: string;
}

export const ZONE_CONFIGS: ZoneConfig[] = [
  { label: 'Z0', barColor: '#D1D5DB', bg: '#F9FAFB', borderColor: '#D1D5DB', textColor: '#9CA3AF' },
  { label: 'Z1', barColor: '#93C5FD', bg: '#EFF6FF', borderColor: '#93C5FD', textColor: '#3B82F6' },
  { label: 'Z2', barColor: '#86EFAC', bg: '#ECFDF5', borderColor: '#86EFAC', textColor: '#22C55E' },
  { label: 'Z3', barColor: '#FCD34D', bg: '#FFFBEB', borderColor: '#FCD34D', textColor: '#CA8A04' },
  { label: 'Z4', barColor: '#FDBA74', bg: '#FFF7ED', borderColor: '#FDBA74', textColor: '#EA580C' },
  { label: 'Z5', barColor: '#FCA5A5', bg: '#FEF2F2', borderColor: '#FCA5A5', textColor: '#EF4444' },
];

export const AVATAR_BG_COLORS = ['#c2185b', '#1565c0', '#00695c', '#e65100', '#6a1b9a', '#2e7d32', '#ad1457'];

export function ownerInitials(owner: { firstname?: string | null; lastname?: string | null }): string {
  const a = owner.firstname?.trim().charAt(0) ?? '';
  const b = owner.lastname?.trim().charAt(0) ?? '';
  return `${a}${b}`.toUpperCase() || '?';
}

export function ownerAvatarColor(ownerKey: number): string {
  return AVATAR_BG_COLORS[Math.abs(ownerKey) % AVATAR_BG_COLORS.length];
}

/**
 * Derives initials from a full name string (e.g. "Jane Doe" → "JD").
 * Falls back to the first two characters if no space is found.
 */
export function getInitials(fullName: string): string {
  const trimmed = fullName.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

/**
 * Derives a deterministic background color from a name string,
 * using the same color palette as ownerAvatarColor.
 */
export function getAvatarColorFromName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_BG_COLORS[Math.abs(hash) % AVATAR_BG_COLORS.length];
}
